import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const KEY = "safe_active_entity";

export function WorkspaceSwitcher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(
    typeof window !== "undefined" ? localStorage.getItem(KEY) : null
  );

  const { data: entities = [] } = useQuery({
    queryKey: ["workspace-entities", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("entities")
        .select("id, name")
        .order("name", { ascending: true })
        .limit(25);
      return (data || []) as { id: string; name: string }[];
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!activeId && entities.length) setActiveId(entities[0].id);
  }, [entities, activeId]);

  const active = entities.find((e) => e.id === activeId) || entities[0];

  if (!entities.length) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 px-3 gap-2 rounded-full text-sm font-medium bg-muted/40 hover:bg-muted/70 max-w-[200px]"
        >
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="truncate">{active?.name || "Workspace"}</span>
          <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1 rounded-2xl">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-3 pt-2 pb-1">
          Switch workspace
        </p>
        <div className="max-h-72 overflow-y-auto">
          {entities.map((e) => (
            <button
              key={e.id}
              onClick={() => {
                setActiveId(e.id);
                localStorage.setItem(KEY, e.id);
                setOpen(false);
              }}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg text-left hover:bg-accent text-foreground"
            >
              <span className="truncate">{e.name}</span>
              {e.id === activeId && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
